// src/pages/FacturesPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DataTable, { ColumnDef } from '../components/table/DataTable';
import { api } from '../services/api';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header'; 

type ApiItem = {
  NUMFACT: string;
  DATFACT: string;
  DATECHEANCE?: string | null;
  TOTALTTC: number;
  SOLDE: number;
  TITREFACT?: string | null;
  TIERSID?: string | number | null;
  NOM?: string | null;
  PRENOM?: string | null;
  NOMBAT?: string | null;
  STATUS?: 'PAYEE' | 'PARTIEL' | 'IMPAYEE';
};

type Row = {
  NUMFACT: string;
  DATFACT: string;
  DATECHEANCE?: string | null;
  CLIENT: string;
  NOMBAT?: string | null;
  TITREFACT?: string | null;
  TOTALTTC: number;
  SOLDE: number;
  STATUS?: 'PAYEE' | 'PARTIEL' | 'IMPAYEE';
};

const STATUSES = ['PAYEE', 'PARTIEL', 'IMPAYEE'] as const;
type Status = typeof STATUSES[number] | 'ALL';

export default function FacturesPage() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  // --- URL status handling
  const { search } = useLocation();
  const navigate = useNavigate();
  const sp = useMemo(() => new URLSearchParams(search), [search]);

  const initialStatus = useMemo<Status>(() => {
    const raw = (sp.get('status') || '').toUpperCase();
    return (STATUSES as readonly string[]).includes(raw) ? (raw as Status) : 'ALL';
  }, [sp]);

  const [statusFilter, setStatusFilter] = useState<Status>(initialStatus);

  // keep state in sync if URL changes externally
  useEffect(() => {
    setStatusFilter(initialStatus);
  }, [initialStatus]);

  // apply filter to rows
  const filteredRows = useMemo(
    () => (statusFilter === 'ALL' ? rows : rows.filter(r => r.STATUS === statusFilter)),
    [rows, statusFilter]
  );

  // fetch data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setErr('');
        setLoading(true);
        const res = await api.get('/factures');
        const items: ApiItem[] = res.data?.items ?? [];
        const mapped: Row[] = items.map(it => {
          const client = [it.NOM ?? '', it.PRENOM ?? ''].map(s => `${s}`.trim()).filter(Boolean).join(' ') || '—';
          return {
            NUMFACT: `${it.NUMFACT ?? ''}`,
            DATFACT: it.DATFACT ?? '',
            DATECHEANCE: it.DATECHEANCE ?? null,
            CLIENT: client,
            NOMBAT: it.NOMBAT ?? null,
            TITREFACT: it.TITREFACT ?? null,
            TOTALTTC: Number(it.TOTALTTC ?? 0),
            SOLDE: Number(it.SOLDE ?? 0),
            STATUS: it.STATUS,
          };
        });
        if (!cancelled) setRows(mapped);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // helpers
  const money = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n ?? 0);

  const cols: ColumnDef<Row>[] = [
    { key: 'NUMFACT', header: 'N° Facture', sortable: true, filter: 'text', width: '140px',
      render: v => <span className="font-medium">{v}</span> },
    { key: 'DATFACT', header: 'Date', sortable: true, filter: 'date', width: '130px',
      render: v => v ? <span className="tabular-nums">{new Date(v).toLocaleDateString('fr-FR')}</span> : '—' },
    { key: 'DATECHEANCE', header: 'Échéance', sortable: true, filter: 'date', width: '130px',
      render: v => v ? <span className="tabular-nums">{new Date(v).toLocaleDateString('fr-FR')}</span> : '—' },
    { key: 'CLIENT', header: 'Client', sortable: true, filter: 'text' },
    { key: 'NOMBAT', header: 'Bateau', sortable: true, filter: 'text', render: v => v || '—' },
    { key: 'TITREFACT', header: 'Titre', sortable: true, filter: 'text', render: v => v || '—' },
    { key: 'TOTALTTC', header: 'TTC', sortable: true, filter: 'number', align: 'right',
      render: v => <span className="tabular-nums">{money(v)}</span> },
    { key: 'SOLDE', header: 'Solde', sortable: true, filter: 'number', align: 'right',
      render: v => <span className="tabular-nums">{money(v)}</span> },
    { key: 'STATUS', header: 'Statut', sortable: true,
      filter: { type: 'select', options: ['PAYEE','PARTIEL','IMPAYEE'] },
      render: v => (
        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs
          ${v==='PAYEE'?'bg-green-100 text-green-700':
            v==='PARTIEL'?'bg-amber-100 text-amber-700':
            'bg-rose-100 text-rose-700'}`}>
          {v ?? '—'}
        </span>
      )
    },
  ];

  // UI: chips to change filter and sync URL
  const setFilterAndUrl = (s: Status) => {
    setStatusFilter(s);
    const next = new URLSearchParams(search);
    if (s === 'ALL') next.delete('status');
    else next.set('status', s);
    navigate({ search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
  };

  if (err) return <div className="p-6 text-red-600">{err}</div>;

  return (
    <div className="min-h-screen flex bg-[#f9fafb]  dark:bg-darkMode">
          <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
          <div className="flex flex-col flex-1 lg:ml-64">
            <Header onOpenSidebar={() => setIsSidebarOpen(true)} />
    
            <main className="p-6 dark:bg-darkMode pt-16">
                <div className="p-6">
                <div className="mb-4 flex items-center justify-between">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Factures</h1>

                    {/* Filtres rapides par statut (synchro URL) */}
                    <div className="flex items-center gap-2">
                    <Chip active={statusFilter==='ALL'}      onClick={() => setFilterAndUrl('ALL')}>Tout</Chip>
                    <Chip active={statusFilter==='PAYEE'}    onClick={() => setFilterAndUrl('PAYEE')}>Payée</Chip>
                    <Chip active={statusFilter==='PARTIEL'}  onClick={() => setFilterAndUrl('PARTIEL')}>Partiel</Chip>
                    <Chip active={statusFilter==='IMPAYEE'}  onClick={() => setFilterAndUrl('IMPAYEE')}>Impayée</Chip>
                    </div>
                </div>

                {loading ? (
                    <div className="rounded-xl border border-gray-200 p-6 text-gray-500">Chargement…</div>
                ) : filteredRows.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 p-6 text-gray-500">Aucune facture.</div>
                ) : (
                    <DataTable
                    data={filteredRows}
                    columns={cols}
                    pageSize={20}
                    initialSort={{ key: 'DATFACT', dir: 'desc' }}
                    onRowClick={(row) => console.log('open facture', row.NUMFACT)}
                    />
                )}
                </div>
            </main>
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border transition
      ${active ? 'bg-indigo-600 text-white border-indigo-600'
               : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:border-white/20  dark:bg-gray-800  dark:text-gray-100'}`}
    >
      {children}
    </button>
  );
}
